import React from 'react';
import { SButton, SIcon, SNotification } from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { FormatConditionValues } from '@bbkAdminComponents/optimizer/optimization-condition-utils';
import { ActionCardDetails } from '@bbkAdminComponents/optimizer/OptimizationActions';
import { OptimizationConfigHelper } from '@bbkAdminComponents/optimizer/OptimizationConfigHelper';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  ApplyChangesEnum,
  OptimizerJobType,
  ScheduledChangesHandlingEnum,
  type GetChangesForSubscriptionResponse,
  type GetJobsResponse,
  type Optimization,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

// Re-export for backward compatibility
export {
  Chip,
  formatConditions,
  FormatConditionValues,
} from '@bbkAdminComponents/optimizer/optimization-condition-utils';

export type StatusFilter =
  | 'all'
  | 'draft'
  | 'in_progress'
  | 'scheduled'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Helper function to normalize status to a common format.
 * Handles both Clockwork job statuses (SUBMITTED, PENDING, RUNNABLE, STARTING, RUNNING, SUCCEEDED, FAILED)
 * and optimization-level statuses (draft, scheduled, in_progress, completed, failed, cancelled)
 */
export const getNormalizedStatus = (
  status: string | undefined
): StatusFilter => {
  if (!status) return 'draft';

  // Normalize to uppercase and replace spaces with underscores for consistent comparison
  const normalized = status.toUpperCase().replace(/\s+/g, '_');

  // Handle Clockwork job statuses and optimization-level statuses
  if (
    normalized === 'SUBMITTED' ||
    normalized === 'PENDING' ||
    normalized === 'RUNNABLE' ||
    normalized === 'STARTING' ||
    normalized === 'RUNNING' ||
    normalized === 'IN_PROGRESS'
  ) {
    return 'in_progress';
  }
  if (
    normalized === 'SUCCEEDED' ||
    normalized === 'COMPLETED' ||
    normalized === 'SUCCESS'
  ) {
    return 'completed';
  }
  if (normalized === 'FAILED') {
    return 'failed';
  }
  if (normalized === 'SCHEDULED') {
    return 'scheduled';
  }
  if (normalized === 'CANCELLED') {
    return 'cancelled';
  }
  return 'draft';
};

/**
 * Gets the latest execution job from jobs data.
 * Returns the job_id of the latest execution job, or undefined if none exists.
 */
export const getLatestExecutionJobId = (
  jobsData: GetJobsResponse | undefined
): string | undefined => {
  if (!jobsData?.collection) return undefined;

  const executionJobs = jobsData.collection.filter(
    (job) => job.job_type === OptimizerJobType.EXECUTION
  );
  if (executionJobs.length === 0) return undefined;

  // Sort by created_at descending to get the latest
  const sorted = [...executionJobs].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  return sorted[0]?.job_id;
};

const CHARGEBEE_HOST = 'chargebee.com';

export const useChargebeeSiteName = (appKey: string): string | undefined => {
  const billingIntegration = useSelectBillingIntegration({ appKey });
  return billingIntegration?.required_input?.find((x) => x.name === 'site')
    ?.value;
};

export const openCbSubscriptionPage = (
  chargebeeSiteName: string,
  subscriptionId: string
) => {
  if (!chargebeeSiteName || !subscriptionId) {
    console.warn(
      'Cannot open Chargebee subscription page: missing site name or subscription ID'
    );
    return;
  }
  window.open(
    `https://${chargebeeSiteName}.${CHARGEBEE_HOST}/d/subscriptions/${subscriptionId}`,
    '_blank'
  );
};

export const useHandleViewSubscription = (appKey: string) => {
  const chargebeeSiteName = useChargebeeSiteName(appKey);
  return (subscriptionId: string) => {
    if (!chargebeeSiteName) {
      BlackToastTrigger({
        content: 'Please add a Chargebee site name to the billing integration',
        type: 'error',
      });
      return;
    }
    if (subscriptionId) {
      openCbSubscriptionPage(chargebeeSiteName, subscriptionId);
    }
  };
};

export const getBillingAlignmentText = (
  type: GetChangesForSubscriptionResponse['billing_alignment']
) => {
  switch (type) {
    case 'immediately':
      return 'Immediately';
    case 'end_of_term':
      return 'On Next Renewal';
    case 'specific_date':
      return 'On Specific Date';
    case 'billing_cycles':
      return 'In _ Billing Cycles';
    default:
      return 'Unknown';
  }
};

export const getScheduledChangesWarning = (
  type: ScheduledChangesHandlingEnum | undefined
) => {
  switch (type) {
    case ScheduledChangesHandlingEnum.DISCARD_EXISTING_SCHEDULES:
      return 'Subscription already has changes scheduled. These existing scheduled changes will be discarded when the optimization is applied.';
    case ScheduledChangesHandlingEnum.RETAIN_EXISTING_SCHEDULES:
      return 'Subscription already has changes scheduled. These existing scheduled changes will be retained when the optimization is applied.';
    case ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS:
      return 'Subscription already has changes scheduled. This subscription will be skipped when the optimization is applied.';
    default:
      return 'Subscription already has changes scheduled. This optimization may have an impact on the existing scheduled changes.';
  }
};

export const getScheduledChangesHandlingDescription = (
  type: ScheduledChangesHandlingEnum | undefined,
  applyChanges: ApplyChangesEnum | undefined
) => {
  const applyAt =
    applyChanges === ApplyChangesEnum.END_OF_TERM
      ? 'at renewal'
      : 'immediately';
  switch (type) {
    case ScheduledChangesHandlingEnum.DISCARD_EXISTING_SCHEDULES:
      return (
        'The optimization is set to discard existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription when the optimization is applied ' +
        applyAt +
        '.'
      );
    case ScheduledChangesHandlingEnum.RETAIN_EXISTING_SCHEDULES:
      return (
        'The optimization is set to retain existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription when the optimization is applied ' +
        applyAt +
        '.'
      );
    case ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS:
      return (
        'The optimization is set to skip subscriptions that already have changes scheduled. This subscription will be skipped when the optimization is applied ' +
        applyAt +
        ', and these existing changes will be scheduled for the subscription.'
      );
    default:
      return 'The optimization may have an impact on the existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription.';
  }
};

const EditButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <SButton
    icon={<SIcon className="size-4" name="pencil" />}
    iconPosition="left"
    variant="primary-outline"
    onClick={onClick}
  >
    Edit
  </SButton>
);

export const OverviewContent: React.FC<{
  optimization: Partial<Optimization>;
  isPreviewPage: boolean;
  onEditAudience?: () => void;
  onEditActions?: () => void;
  onEditConfig?: () => void;
}> = ({
  optimization,
  isPreviewPage,
  onEditAudience,
  onEditActions,
  onEditConfig,
}) => {
  const applyImmediately =
    (optimization.configurations?.apply_changes ??
      ApplyChangesEnum.IMMEDIATELY) === ApplyChangesEnum.IMMEDIATELY;
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-md">
      {isPreviewPage && (
        <SNotification
          description="We'll show you a sample of affected subscribers so you can verify your rules are working correctly before publishing to all subscribers."
          title="Preview sample changes before publishing?"
          variant="warning"
          hasBorder={false}
        />
      )}

      <div className="tw-flex tw-w-full tw-flex-col tw-gap-md tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-md">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
          <div className="tw-mb-1 tw-font-medium">Audience</div>
          <div>{isPreviewPage && <EditButton onClick={onEditAudience} />}</div>
        </div>
        <div>
          {optimization.audience && (
            <FormatConditionValues condition={optimization.audience} />
          )}
        </div>
      </div>

      <div className="tw-flex tw-w-full tw-flex-col tw-gap-md tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-md">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
          <div className="tw-mb-1 tw-font-medium">Actions</div>
          <div>{isPreviewPage && <EditButton onClick={onEditActions} />}</div>
        </div>
        <div>
          {optimization.actions && (
            <ActionCardDetails actions={optimization.actions} />
          )}
        </div>
      </div>

      <div className="tw-flex tw-w-full tw-flex-col tw-gap-md tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-md">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
          <div className="tw-mb-1 tw-font-medium">Configurations</div>
          <div>{isPreviewPage && <EditButton onClick={onEditConfig} />}</div>
        </div>
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
          <div className="tw-flex tw-flex-col">
            <div className="tw-mb-1 tw-text-sm">Changes take effect</div>
            <div className="tw-mb-1 tw-font-medium">
              {OptimizationConfigHelper.getApplyChangesLabel(
                optimization.configurations?.apply_changes ??
                  ApplyChangesEnum.IMMEDIATELY
              )}
            </div>
            {applyImmediately && (
              <div>
                <div className="tw-mb-1 tw-mt-md tw-text-sm">
                  Scheduled Change Handling
                </div>
                <div className="tw-mb-1 tw-font-medium">
                  {OptimizationConfigHelper.getChangeHandlingLabel(
                    optimization.configurations?.scheduled_changes_handling
                  )}
                </div>
              </div>
            )}
          </div>
          {applyImmediately && (
            <div>
              <div className="tw-mb-1 tw-text-sm">
                Mid-cycle billing options
              </div>
              <div className="tw-mb-1 tw-font-medium">
                {OptimizationConfigHelper.getProrationLabel(
                  optimization.configurations?.mid_cycle_billing
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getVisiblePageNumbers = (currentPage: number, totalPages: number) => {
  const pages: (number | '...')[] = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else if (currentPage >= totalPages - 3) {
    for (let i = totalPages - 4; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    const start = Math.max(1, currentPage - 1);
    const end = Math.min(start + 1, totalPages - 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(totalPages - 1);
    pages.push(totalPages);
  }
  return pages;
};

export const OptimizationPaginationFooter: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  additionalClassName: string;
  setCurrentPage: (page: number) => void;
}> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  setCurrentPage,
  additionalClassName,
}) => {
  const visiblePageNumbers = getVisiblePageNumbers(currentPage, totalPages);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div
      className={`${additionalClassName} tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-neutral-200 tw-bg-white tw-py-sm`}
    >
      <div className="tw-text-sm tw-font-normal tw-text-neutral-500">
        Showing {startIndex} - {endIndex} of {totalItems}
      </div>
      <div className="tw-flex !tw-justify-items-end tw-gap-2">
        <SButton
          variant="primary-ghost"
          icon={<SIcon name="chevron-left" size={14} />}
          iconPosition="left"
          disabled={currentPage === 1}
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          className="!tw-border-0 !tw-bg-white !tw-p-0"
        >
          Previous
        </SButton>
        {visiblePageNumbers.map((page, index) => {
          return (
            <SButton
              key={`page-${page}`}
              variant={
                page === '...'
                  ? 'neutral-ghost'
                  : currentPage === page
                    ? 'primary-outline'
                    : 'neutral'
              }
              onClick={() => typeof page === 'number' && setCurrentPage(page)}
            >
              {page}
            </SButton>
          );
        })}
        <SButton
          variant="primary-ghost"
          icon={<SIcon name="chevron-right" size={14} />}
          iconPosition="right"
          disabled={currentPage === totalPages}
          onClick={() =>
            currentPage < totalPages && setCurrentPage(currentPage + 1)
          }
          className="!tw-border-0 !tw-bg-white !tw-p-0"
        >
          Next
        </SButton>
      </div>
    </div>
  );
};
